import { Suspense, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Route, Routes } from "react-router-dom"
import { ROUTES } from "@/helpers/routes"
import { safeLazy } from "@/helpers/safeLazy"
import { getTgUserId, getTgWebApp } from "@/helpers/tgWebApp"
import { useActiveEffectExpiry } from "@/hooks/useActiveEffectExpiry"
import { useAutoAuth } from "@/hooks/useAutoAuth"
import { useHasSession } from "@/hooks/useHasSession"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { useLoad } from "@/hooks/useLoad"
import { NotFound } from "@/pages/404"
import { Login } from "@/pages/Login"
import { OAuthCallback } from "@/pages/OAuthCallback"
import { OAuthError } from "@/pages/OAuthError"
import { Register } from "@/pages/Register"
import { useAppDispatch, useAppSelector } from "@/store"
import {
  AppSelectors,
  resolveIsTMA,
  setIsPhoneDevice,
  setIsTMA,
  setLanguage,
  setReAuthState
} from "@/store/slices/app"
import { DevToolsSelectors } from "@/store/slices/devTools"
import { PlayerSelectors } from "@/store/slices/player"
import { PageLoader } from "@/ui/PageLoader"
import { TelegramAuthError } from "@/ui/TelegramAuthError"

const WorldMap = safeLazy(() =>
  import("@/pages/WorldMap").then((m) => ({ default: m.WorldMap }))
)

const RouterAuth = () => (
  <Routes>
    <Route index element={<Navigate to={ROUTES.LOGIN} replace />} />
    <Route path="login" element={<Login />} />
    <Route path="register" element={<Register />} />
    <Route path="oauth/callback" element={<OAuthCallback />} />
    <Route path="oauth/error" element={<OAuthError />} />
    <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
  </Routes>
)

const RouterApp = () => {
  useLoad()
  const isPlayerLoaded = useAppSelector(PlayerSelectors.selectIsPlayerLoaded())

  if (!isPlayerLoaded) {
    return <PageLoader />
  }

  return (
    <Routes>
      {/* Full-viewport seam: the world map renders full-bleed at the root,
          outside any padded content layout. */}
      <Route
        index
        element={
          <Suspense fallback={<PageLoader />}>
            <WorldMap />
          </Suspense>
        }
      />
      <Route
        path={ROUTES.LOGIN}
        element={<Navigate to={ROUTES.HOME} replace />}
      />
      <Route
        path={ROUTES.REGISTER}
        element={<Navigate to={ROUTES.HOME} replace />}
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

// version-gating: backend exposes only GET /health, no version endpoint —
// pattern inactive (spec §5.9)
export const Router = () => {
  const dispatch = useAppDispatch()
  const { i18n } = useTranslation()

  useIsDesktop()
  useAutoAuth()
  useActiveEffectExpiry()

  const hasSession = useHasSession()
  const isTMA = useAppSelector(AppSelectors.selectIsTMA())
  const reAuthState = useAppSelector(AppSelectors.selectReAuthState())
  const tmaOverride = useAppSelector(DevToolsSelectors.selectTmaOverride())
  const emulatePhoneDevice = useAppSelector(
    DevToolsSelectors.selectEmulatePhoneDevice()
  )

  useEffect(() => {
    dispatch(setIsTMA(resolveIsTMA(tmaOverride, getTgUserId() !== undefined)))
  }, [tmaOverride, dispatch])

  // Sync app.language with the detected i18n language (the detector is the source).
  useEffect(() => {
    dispatch(setLanguage(i18n.language))
  }, [i18n.language, dispatch])

  // Phone-device detection.
  useEffect(() => {
    const platform = getTgWebApp()?.platform
    const realIsPhone = platform === "android" || platform === "ios"
    const isPhoneDevice = import.meta.env.DEV ? emulatePhoneDevice : realIsPhone
    dispatch(setIsPhoneDevice(isPhoneDevice))
  }, [emulatePhoneDevice, dispatch])

  const retryAutoAuth = () => dispatch(setReAuthState({ kind: "idle" }))
  const closeMiniApp = () => getTgWebApp()?.close()

  if (hasSession) {
    return <RouterApp />
  }

  if (isTMA) {
    return reAuthState.kind === "error" ? (
      <TelegramAuthError
        cause={reAuthState.cause}
        onRetry={retryAutoAuth}
        onClose={closeMiniApp}
      />
    ) : (
      <PageLoader />
    )
  }

  return <RouterAuth />
}
